import { Injectable } from '@angular/core'
import { AuthService } from '../../services/auth.service'
import { AdminPrefsService } from './admin-prefs.service'
import { matchesQuery } from './arabic-text'
import { NAV_ITEMS } from './nav.config'
import { NavGroup, NavItem, NavLeaf, PageKey, isNavGroup } from './nav.model'

export interface NavMatch {
  leaf: NavLeaf
  /** Ancestor groups, outermost first. Doubles as the breadcrumb trail. */
  path: NavGroup[]
}

@Injectable({ providedIn: 'root' })
export class NavigationService {
  private visible: NavItem[] | null = null
  private flattened: NavMatch[] | null = null

  constructor(
    private auth: AuthService,
    private prefs: AdminPrefsService,
  ) {}

  /**
   * The menu this admin may see.
   *
   * Computed once and cached. The template it replaced called hasPageAccess()
   * from 31 `*ngIf` expressions, so the entire permission table was re-evaluated
   * on every change-detection cycle.
   */
  get visibleNav(): NavItem[] {
    if (!this.visible) {
      this.visible = this.build(NAV_ITEMS)
    }
    return this.visible
  }

  /** Every reachable destination with its ancestry — the corpus behind search and breadcrumbs. */
  get matches(): NavMatch[] {
    if (!this.flattened) {
      this.flattened = this.flatten(this.visibleNav, [])
    }
    return this.flattened
  }

  get visibleLeaves(): NavLeaf[] {
    return this.matches.map((match) => match.leaf)
  }

  /** Permissions only change when the token does, so this is called on login/logout. */
  reset(): void {
    this.visible = null
    this.flattened = null
    this.prefs.reset()
  }

  /**
   * Which menu entry a URL belongs to — drives the active highlight, which groups
   * start expanded, the breadcrumb trail and recents.
   */
  findByUrl(url: string): NavMatch | null {
    const path = (url || '').split('?')[0].split('#')[0]
    let best: NavMatch | null = null

    for (const match of this.matches) {
      // Segment-boundary match, never a bare startsWith: `/entertainment` is a
      // string prefix of `/entertainment-alhokair/list` but not its parent.
      const route = match.leaf.route
      const isMatch = path === route || path.startsWith(`${route}/`)
      if (isMatch && (!best || route.length > best.leaf.route.length)) {
        best = match
      }
    }

    return best
  }

  search(query: string): NavMatch[] {
    return this.matches.filter(({ leaf, path }) =>
      matchesQuery(query, [
        leaf.label,
        leaf.route,
        ...(leaf.keywords || []),
        ...path.map((group) => group.label),
      ]),
    )
  }

  /**
   * The visible children of one group, which is what a tabbed hub renders as its
   * tabs. Deriving them from the same config the sidebar uses means a hub can
   * never show a tab the menu has hidden, or drift out of order.
   */
  groupChildren(groupId: string): NavLeaf[] {
    const group = this.findGroup(this.visibleNav, groupId)
    if (!group) return []
    return group.children.filter((child): child is NavLeaf => !isNavGroup(child))
  }

  private findGroup(items: NavItem[], groupId: string): NavGroup | null {
    for (const item of items) {
      if (!isNavGroup(item)) continue
      if (item.id === groupId) return item
      const nested = this.findGroup(item.children, groupId)
      if (nested) return nested
    }
    return null
  }

  /** Pinned pages, in pin order, dropping any the admin can no longer reach. */
  get favorites(): NavLeaf[] {
    return this.resolve(this.prefs.prefs.favorites)
  }

  /** Most recent first, excluding anything already pinned. */
  get recents(): NavLeaf[] {
    const pinned = new Set(this.prefs.prefs.favorites)
    return this.resolve(this.prefs.prefs.recents).filter((leaf) => !pinned.has(leaf.id))
  }

  recordVisit(url: string): void {
    const match = this.findByUrl(url)
    if (match) {
      this.prefs.recordVisit(match.leaf.id)
    }
  }

  /**
   * Ids are resolved against the visible tree, so a page that was pinned and later
   * revoked simply disappears instead of rendering a link that dead-ends on a guard.
   */
  private resolve(ids: string[]): NavLeaf[] {
    const byId = new Map(this.matches.map(({ leaf }) => [leaf.id, leaf]))
    return ids.map((id) => byId.get(id)).filter((leaf): leaf is NavLeaf => !!leaf)
  }

  private build(items: NavItem[]): NavItem[] {
    const result: NavItem[] = []

    for (const item of items) {
      if (!isNavGroup(item)) {
        if (this.canSee(item.pageKey)) result.push(item)
        continue
      }

      if (!this.canSee(item.pageKey)) continue

      const children = this.build(item.children)
      // A group whose children are all hidden is an empty dropdown, so it is dropped
      // rather than rendered. This is what lets sections be pure containers: give a
      // group no pageKey of its own and its children decide whether it appears.
      if (children.length) result.push({ ...item, children })
    }

    return result
  }

  private flatten(items: NavItem[], path: NavGroup[]): NavMatch[] {
    return items.flatMap((item) =>
      isNavGroup(item) ? this.flatten(item.children, [...path, item]) : [{ leaf: item, path }],
    )
  }

  /** No key means "inherit the parent's gate", which the caller has already checked. */
  private canSee(pageKey?: PageKey): boolean {
    if (!pageKey) return true
    const keys = Array.isArray(pageKey) ? pageKey : [pageKey]
    return keys.some((key) => this.auth.hasPageAccess(key))
  }
}
